import React, { useEffect, useRef } from 'react';
import videojs from 'video.js';
import 'video.js/dist/video-js.css';
import '../assets/css/video-js-forest.css';
import { useSelector } from "react-redux";

/* ─── Global coordinator: only the video closest to viewport center plays ─── */
const videoInstances = new Set();
let scrollRAF = null;

function updateActiveVideo() {
  if (scrollRAF) return;
  scrollRAF = requestAnimationFrame(() => {
    scrollRAF = null;
    const center = window.innerHeight / 2;
    let bestInstance = null;
    let bestDist = Infinity;

    videoInstances.forEach((inst) => {
      if (!inst.el || !inst.player || inst.player.isDisposed()) return;
      const rect = inst.el.getBoundingClientRect();
      // Must be at least 50% visible
      const visibleTop = Math.max(rect.top, 0);
      const visibleBot = Math.min(rect.bottom, window.innerHeight);
      const visibleH = visibleBot - visibleTop;
      if (visibleH < rect.height * 0.5) return;
      // Distance from center
      const mid = rect.top + rect.height / 2;
      const dist = Math.abs(mid - center);
      if (dist < bestDist) {
        bestDist = dist;
        bestInstance = inst;
      }
    });

    videoInstances.forEach((inst) => {
      if (!inst.player || inst.player.isDisposed()) return;
      if (inst === bestInstance) {
        inst.player.play().catch(() => { });
      } else {
        inst.player.pause();
      }
    });
  });
}

const VideoJS = ({ options, onReady, enableScrollPause }) => {
  const videoRef = useRef(null);
  const playerRef = useRef(null);
  const selector = useSelector((state) => state.settingsReducer);
  const settingsData = selector?.data;

  // ─── Scroll-pause: register with global coordinator ───
  useEffect(() => {
    if (enableScrollPause && settingsData.allowVideoAutoPlay) {
      const inst = { el: videoRef.current, player: playerRef.current };

      // Register once player is ready
      const checkReady = setInterval(() => {
        if (playerRef.current) {
          inst.player = playerRef.current;
          videoInstances.add(inst);
          updateActiveVideo();
          clearInterval(checkReady);
        }
      }, 100);

      window.addEventListener('scroll', updateActiveVideo, { passive: true });

      return () => {
        clearInterval(checkReady);
        videoInstances.delete(inst);
        window.removeEventListener('scroll', updateActiveVideo);
      };
    }
  }, [enableScrollPause]);

  // ─── Initialize or update video.js player ───
  useEffect(() => {
    if (!playerRef.current) {
      const videoElement = document.createElement('video-js');
      videoElement.classList.add('w-100', 'vjs-theme-forest', 'rounded-3');
      videoRef.current.appendChild(videoElement);

      const player = playerRef.current = videojs(videoElement, options, () => {
        if (onReady) {
          onReady(player);
        }
      });

      if (options.name) {
        // Generate JSON-LD markup for the video
        const videoMarkup = {
          '@context': 'https://schema.org/',
          '@type': 'VideoObject',
          name: options.name,
          uploadDate: options.uploadDate,
          embedUrl: options.embedUrl,
          thumbnailUrl: options.poster,
          contentUrl: options.sources[0].src
        };

        const script = document.createElement('script');
        script.type = 'application/ld+json';
        script.innerHTML = JSON.stringify(videoMarkup);
        document.head.appendChild(script);
      }

    } else {
      const player = playerRef.current;
      player.autoplay(options.autoplay);
      player.src(options.sources);
    }
    // Depend on the actual source URL + autoplay rather than the `options`
    // object reference. Parents (MediaCarousel, PostNew, etc.) pass a fresh
    // `options={{...}}` literal on every render, and the previous deps
    // `[options, videoRef]` made this effect fire on every parent re-render,
    // which called `player.src()` and reset playback. The `else` branch only
    // touches src + autoplay, so those are the only meaningful triggers.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options?.sources?.[0]?.src, options?.autoplay]);

  // ─── Cleanup on unmount ───
  useEffect(() => {
    const player = playerRef.current;

    return () => {
      if (player && !player.isDisposed()) {
        player.dispose();
        playerRef.current = null;

        if (options.name) {
          const script = document.querySelector('script[type="application/ld+json"]');
          if (script) {
            script.remove();
          }
        }
      }
    };
  }, [playerRef]);

  return (
    <div data-vjs-player>
      <div ref={videoRef} />
    </div>
  );
};

export default VideoJS;
